import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';

import SearchIcon from '../../icons/Search';
import Button from '../Button';
import PlainInput from '../PlainInput';

import cssStyles from './SearchBox.module.scss';

const SearchBox = ({
  placeholder,
  value,
  onSearch,
  searchBtnProps,
  debounceTime = 0,
  submitOnEnter = false,
  ...otherProps
}) => {
  const [term, setTerm] = useState(value || '');

  const manualSearch = useCallback(
    (searchTerm) => {
      onSearch(searchTerm);
    },
    [onSearch],
  );

  const triggerManualSearch = useCallback(() => {
    manualSearch(term);
  }, [term, manualSearch]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!submitOnEnter) return;
      if (event.key === 'Enter') {
        manualSearch(event.target.value);
      }
    },
    [submitOnEnter, manualSearch],
  );

  const propagateChange = useCallback(
    debounce(manualSearch, debounceTime || 0),
    [manualSearch, debounceTime],
  );

  const handleChange = useCallback(
    (e) => {
      setTerm(e.target.value);
      if (debounceTime > 0) {
        propagateChange(e.target.value);
      }
    },
    [debounceTime],
  );

  useEffect(() => {
    setTerm(value);
  }, [value]);

  return (
    <div className={cssStyles.container}>
      <div className={cssStyles.inputWrapper}>
        <SearchIcon width={20} height={20} />
        <PlainInput
          type="text"
          onKeyDown={handleKeyDown}
          id={`searchbox`}
          name={`searchbox`}
          {...otherProps}
          className={cssStyles.textInput}
          value={term}
          placeholder={placeholder || `Search...`}
          onChange={handleChange}
        />
      </div>
      {!debounceTime && (
        <Button
          small
          secondary
          className={cssStyles.button}
          {...(searchBtnProps?.type === 'submit'
            ? searchBtnProps
            : { onClick: triggerManualSearch })}
        >
          Search
        </Button>
      )}
    </div>
  );
};

SearchBox.propTypes = {
  searchBtnProps: PropTypes.shape({}),
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  debounceTime: PropTypes.number,
};

export default SearchBox;
